import type { FormFieldAdapter } from "../adapters/mds-form-field";

export interface DraftPayload {
  shipment_type: string;
  shipper_name: string;
  shipper_email: string | null;
  shipper_address: string;
  shipper_tax_id: string;
  consignee_name: string;
  consignee_email: string | null;
  consignee_address: string;
  consignee_tax_id: string;
  notify_name: string;
  notify_email: string | null;
  notify_address: string;
  notify_tax_id: string;
  second_party_name: string;
  second_party_email: string | null;
  second_party_address: string;
  second_party_tax_id: string;
  port_of_loading: string;
  port_of_discharge: string;
  destination_country: string;
  booking_number: string;
  place_of_delivery: string;
  place_of_receipt: string;
  vessel: string;
  voyage: string;
  shipment_mode: string;
  total_gross_weight: string;
  total_net_weight: string;
  total_cbm: string;
  total_quantity: string;
  package_type: string;
  wooden_package: string;
  bl_issuance: string;
  hs_code: string;
  ncm: string;
  due_number: string;
  marks_notes: string;
  cargo_description: string;
  containers: ContainerPayload[];
}

export interface ContainerPayload {
  container_number: string;
  tare: string;
  seal: string;
  net_weight: string;
  gross_weight: string;
  vgm: string;
  weighing_date: string;
  declaration_owner: string;
  item_quantity: string;
  item_type: string;
  cbm: string;
  container_type: string;
}

export interface ContainerRoot {
  children: ArrayLike<{
    querySelector(selector: string): { value?: unknown } | null;
  }>;
}

export interface BuildDraftPayloadOptions {
  fields: Pick<FormFieldAdapter, "readFieldValue">;
  shipmentType: string;
  containersRoot: ContainerRoot;
  sanitizeFreeformTextField?: (value: string) => string;
}

const payloadFieldNames = [
  "shipment_type",
  "shipper_name",
  "shipper_email",
  "shipper_address",
  "shipper_tax_id",
  "consignee_name",
  "consignee_email",
  "consignee_address",
  "consignee_tax_id",
  "notify_name",
  "notify_email",
  "notify_address",
  "notify_tax_id",
  "second_party_name",
  "second_party_email",
  "second_party_address",
  "second_party_tax_id",
  "port_of_loading",
  "port_of_discharge",
  "destination_country",
  "booking_number",
  "place_of_delivery",
  "place_of_receipt",
  "vessel",
  "voyage",
  "shipment_mode",
  "total_gross_weight",
  "total_net_weight",
  "total_cbm",
  "total_quantity",
  "package_type",
  "wooden_package",
  "bl_issuance",
  "hs_code",
  "ncm",
  "due_number"
] as const;

const containerFieldNames = [
  "container_number",
  "tare",
  "seal",
  "net_weight",
  "gross_weight",
  "vgm",
  "weighing_date",
  "declaration_owner",
  "item_quantity",
  "item_type",
  "cbm",
  "container_type"
] as const;

function readTrimmed(fields: Pick<FormFieldAdapter, "readFieldValue">, name: string): string {
  return String(fields.readFieldValue(name) || "").trim();
}

function readRaw(fields: Pick<FormFieldAdapter, "readFieldValue">, name: string): string {
  const value = fields.readFieldValue(name);
  return value === null || value === undefined ? "" : String(value);
}

function toNullableEmail(value: string): string | null {
  const trimmed = value.trim();
  return trimmed || null;
}

function collectContainerValues(shipmentType: string, containersRoot: ContainerRoot): ContainerPayload[] {
  if (shipmentType !== "FCL") {
    return [];
  }

  return Array.from(containersRoot.children, card => {
    const readContainerField = (fieldName: string): string => {
      const field = card.querySelector(`[data-field="${fieldName}"]`);
      return String(field?.value || "").trim();
    };

    return Object.fromEntries(containerFieldNames.map(fieldName => [fieldName, readContainerField(fieldName)])) as unknown as ContainerPayload;
  });
}

export function buildDraftPayload({
  fields,
  shipmentType,
  containersRoot,
  sanitizeFreeformTextField = value => value
}: BuildDraftPayloadOptions): DraftPayload {
  const values = Object.fromEntries(payloadFieldNames.map(name => [name, readTrimmed(fields, name)])) as Record<string, string>;

  return {
    shipment_type: values.shipment_type,
    shipper_name: values.shipper_name,
    shipper_email: toNullableEmail(values.shipper_email),
    shipper_address: values.shipper_address,
    shipper_tax_id: values.shipper_tax_id,
    consignee_name: values.consignee_name,
    consignee_email: toNullableEmail(values.consignee_email),
    consignee_address: values.consignee_address,
    consignee_tax_id: values.consignee_tax_id,
    notify_name: values.notify_name,
    notify_email: toNullableEmail(values.notify_email),
    notify_address: values.notify_address,
    notify_tax_id: values.notify_tax_id,
    second_party_name: values.second_party_name,
    second_party_email: toNullableEmail(values.second_party_email),
    second_party_address: values.second_party_address,
    second_party_tax_id: values.second_party_tax_id,
    port_of_loading: values.port_of_loading,
    port_of_discharge: values.port_of_discharge,
    destination_country: values.destination_country,
    booking_number: values.booking_number,
    place_of_delivery: values.place_of_delivery,
    place_of_receipt: values.place_of_receipt,
    vessel: values.vessel,
    voyage: values.voyage,
    shipment_mode: values.shipment_mode,
    total_gross_weight: values.total_gross_weight,
    total_net_weight: values.total_net_weight,
    total_cbm: values.total_cbm,
    total_quantity: values.total_quantity,
    package_type: values.package_type,
    wooden_package: values.wooden_package,
    bl_issuance: values.bl_issuance,
    hs_code: values.hs_code,
    ncm: values.ncm,
    due_number: values.due_number,
    marks_notes: sanitizeFreeformTextField(readRaw(fields, "marks_notes")),
    cargo_description: sanitizeFreeformTextField(readRaw(fields, "cargo_description")),
    containers: collectContainerValues(shipmentType, containersRoot)
  };
}

export { collectContainerValues, toNullableEmail };
